import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ScrollView,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
  type StyleProp,
  type ViewStyle,
} from "react-native";

type Props<T> = {
  items: T[];
  gap?: number;
  fallbackCardWidth?: number;
  getCardWidth?: (containerWidth: number) => number;
  keyExtractor: (item: T, index: number) => string;
  renderItem: (
    item: T,
    cardWidth: number,
    index: number,
    equalizedHeight?: number,
  ) => ReactNode;
  style?: StyleProp<ViewStyle>;
  contentContainerStyle?: StyleProp<ViewStyle>;
  equalizeItemHeights?: boolean;
  measureKey?: string;
  /**
   * Which item is showing, for callers that render a position indicator.
   *
   * Optional so no existing caller changes: a carousel that shows one card at a
   * time needs to say where the reader is, and one that shows a row of peeking
   * cards does not.
   */
  onActiveIndexChange?: (index: number) => void;
};

export function HorizontalSnapCarousel<T>({
  items,
  gap = 12,
  fallbackCardWidth = 236,
  getCardWidth,
  keyExtractor,
  renderItem,
  style,
  contentContainerStyle,
  equalizeItemHeights = false,
  measureKey,
  onActiveIndexChange,
}: Props<T>) {
  const [containerWidth, setContainerWidth] = useState(0);
  const [itemHeights, setItemHeights] = useState<Record<string, number>>({});
  const cardWidth =
    containerWidth && getCardWidth
      ? getCardWidth(containerWidth)
      : fallbackCardWidth;
  const snapInterval = cardWidth + gap;
  const itemKeys = useMemo(
    () => items.map((item, index) => keyExtractor(item, index)),
    [items, keyExtractor],
  );
  const itemSignature = itemKeys.join("|");
  const measuredHeights = itemKeys
    .map((key) => itemHeights[key])
    .filter((height): height is number => typeof height === "number");
  const equalizedHeight =
    equalizeItemHeights &&
    itemKeys.length > 0 &&
    measuredHeights.length === itemKeys.length
      ? Math.max(...measuredHeights)
      : undefined;

  useEffect(() => {
    setItemHeights({});
  }, [cardWidth, equalizeItemHeights, itemSignature, measureKey]);

  return (
    <View
      onLayout={(e) => setContainerWidth(e.nativeEvent.layout.width)}
      style={style}
    >
      <ScrollView
        horizontal
        decelerationRate="fast"
        // Only when someone is listening. React Native forwards this to the
        // native view regardless of whether the caller passed `onScroll`, so
        // setting it unconditionally would make every existing carousel emit a
        // bridge event every 16ms through a whole drag and discard it.
        scrollEventThrottle={onActiveIndexChange ? 16 : undefined}
        onScroll={
          onActiveIndexChange
            ? (event: NativeSyntheticEvent<NativeScrollEvent>) => {
                // Rounded against the snap interval rather than tracked from
                // the momentum end, so the indicator follows a drag instead of
                // waiting for it to settle.
                const offset = event.nativeEvent.contentOffset.x;
                const index = Math.round(offset / Math.max(snapInterval, 1));
                onActiveIndexChange(
                  Math.min(Math.max(index, 0), Math.max(items.length - 1, 0)),
                );
              }
            : undefined
        }
        snapToInterval={snapInterval}
        snapToAlignment="start"
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={[{ gap }, contentContainerStyle]}
      >
        {items.map((item, index) => (
          <View
            key={itemKeys[index]}
            onLayout={
              equalizeItemHeights
                ? (event) => {
                    const height = Math.ceil(event.nativeEvent.layout.height);
                    const key = itemKeys[index];
                    if (!key) return;
                    setItemHeights((current) =>
                      current[key] === height
                        ? current
                        : { ...current, [key]: height },
                    );
                  }
                : undefined
            }
            style={equalizedHeight ? { minHeight: equalizedHeight } : undefined}
          >
            {renderItem(item, cardWidth, index, equalizedHeight)}
          </View>
        ))}
      </ScrollView>
    </View>
  );
}
